import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Grid,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Divider,
    Chip,
} from '@mui/material';
import { ArrowBack, TrendingUp } from '@mui/icons-material';
import {
    useWordbookGroupOverviewStats,
    useWordbookGroupLanguageStats,
} from '../../hooks/useWordbookGroup';

export const WordbookGroupStats: FC = () => {
    const navigate = useNavigate();
    const { data: overviewStats, isLoading: overviewLoading, error: overviewError } = useWordbookGroupOverviewStats();
    const { data: languageStats, isLoading: languageLoading, error: languageError } = useWordbookGroupLanguageStats();

    if (overviewLoading || languageLoading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" p={4} gap={2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    통계 데이터 로딩 중...
                </Typography>
            </Box>
        );
    }

    if (overviewError || languageError) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {overviewError?.message || languageError?.message || '통계 데이터를 불러오는데 실패했습니다.'}
                </Alert>
            </Box>
        );
    }

    const n = (value: unknown): number => Number(value ?? 0);
    const overview = overviewStats ?? ({} as NonNullable<typeof overviewStats>);
    const languagePairs = languageStats?.languagePairs ?? [];

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/wordbook-group')}
                >
                    목록으로
                </Button>
                <Typography variant="h4">
                    그룹 통계
                </Typography>
            </Box>

            {/* 전체 통계 */}
            <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">
                                전체 그룹 수
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {n(overview.totalGroups).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                활성: {n(overview.activeGroups).toLocaleString()} /
                                삭제: {n(overview.deletedGroups).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">
                                총 단어장 수
                            </Typography>
                            <Typography variant="h4" color="secondary">
                                {n(overview.totalWordbooks).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                그룹당 평균: {n(overview.averageWordbooksPerGroup).toFixed(1)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">
                                총 단어 수
                            </Typography>
                            <Typography variant="h4" color="success.main">
                                {n(overview.totalWords).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                그룹당 평균: {n(overview.averageWordsPerGroup).toFixed(1)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">
                                라이브러리 그룹
                            </Typography>
                            <Typography variant="h4" color="info.main">
                                {n(overview.libraryGroups).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                전체의 {n(overview.totalGroups) > 0 ?
                                ((n(overview.libraryGroups) / n(overview.totalGroups)) * 100).toFixed(1) : 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 노출 상태 통계 */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        노출 상태 분포
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Chip label="노출" color="success" />
                                <Typography variant="h5">
                                    {n(overview.visibleGroups).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({n(overview.totalGroups) > 0 ?
                                    ((n(overview.visibleGroups) / n(overview.totalGroups)) * 100).toFixed(1) : 0}%)
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Chip label="숨김" color="default" />
                                <Typography variant="h5">
                                    {n(overview.hiddenGroups).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({n(overview.totalGroups) > 0 ?
                                    ((n(overview.hiddenGroups) / n(overview.totalGroups)) * 100).toFixed(1) : 0}%)
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* 언어별 통계 */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            언어 쌍별 통계
                        </Typography>
                        <Chip
                            icon={<TrendingUp />}
                            label={`${n(languageStats?.totalPairs)}개 언어 쌍`}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {languagePairs.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>순위</TableCell>
                                    <TableCell>언어 쌍</TableCell>
                                    <TableCell align="right">그룹 수</TableCell>
                                    <TableCell align="right">단어장 수</TableCell>
                                    <TableCell align="right">총 단어 수</TableCell>
                                    <TableCell align="right">라이브러리</TableCell>
                                    <TableCell align="right">비율</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {languagePairs.map((pair, index) => (
                                    <TableRow key={`${pair.headLang}-${pair.meaningLang}`} hover>
                                        <TableCell>
                                            <Chip
                                                label={index + 1}
                                                size="small"
                                                color={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {pair.headLang} → {pair.meaningLang}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                label={n(pair.totalGroups).toLocaleString()}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {n(pair.totalWordbooks).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {n(pair.totalWords).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="text.secondary">
                                                {n(pair.libraryGroups)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="text.secondary">
                                                {n(overview.totalGroups) > 0 ?
                                                    ((n(pair.totalGroups) / n(overview.totalGroups)) * 100).toFixed(1) : 0}%
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography color="text.secondary" textAlign="center" py={4}>
                            통계 데이터가 없습니다.
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
